import React, { useState, useRef, useEffect, useContext } from 'react';
import { Paper, InputBase, Button, Typography, CircularProgress } from '@material-ui/core';
import { makeStyles, Theme, createStyles, useTheme, alpha } from '@material-ui/core/styles';
import { colors } from '../styles/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import { faSparkles } from '@fortawesome/pro-light-svg-icons';
import { LanguageContext } from '../contexts/LanguageContext/languageContext';
import { useUserInfo } from '@terragotech/gen5-shared-components';

interface AskAIProps {
  onSearch?: (query: string) => void;
  onStop?: () => void;
  open: boolean;
  valid?: boolean;
  loading: boolean;
  currentValue?: string;
  setOpen: (newVal: boolean) => void;
  isMobileView?: boolean;
  tableWidth?: number;
}

interface StyleProps {
  isOpen: boolean;
  loading: boolean;
  valid?: boolean;
  isMobileView?: boolean;
  currentValue?: string;
  iconOnly: boolean;
}

const useStyles = makeStyles((theme: Theme) => {
  const standardColor = (props: StyleProps) =>
    !props.isOpen && (props.currentValue === '' || props.currentValue === undefined)
      ? colors.black54
      : theme.palette.primary.main;

  return createStyles({
    container: {
      display: 'flex',
      borderRadius: '5px',
      alignItems: 'stretch',
      height: 34,
      border: '1px solid',
      overflow: 'hidden',
      transition: 'width .5s ease-in-out',
      // Dynamic styles based on props
      minWidth: (props: StyleProps) => (!props.isOpen && (props.isMobileView || props.iconOnly) ? '56px' : '116px'),
      width: (props: StyleProps) => (props.isOpen ? '100%' : props.isMobileView || props.iconOnly ? '56px' : '116px'),
      borderColor: (props: StyleProps) =>
        !props.isOpen && (props.currentValue === '' || props.currentValue === undefined)
          ? colors.black10
          : theme.palette.primary.main,
      boxShadow: (props: StyleProps) =>
        !props.isOpen && (props.currentValue === '' || props.currentValue === undefined)
          ? 'none'
          : `0 0 5px ${theme.palette.primary.main}`,
      cursor: (props: StyleProps) => (props.isOpen ? 'default' : 'pointer'),
      color: (props: StyleProps) => standardColor(props),
      '&:hover': {
        backgroundColor: (props: StyleProps) =>
          props.isMobileView ? 'none' : props.isOpen ? theme.palette.background.paper : theme.palette.grey[200],
      },
    },
    icon: {
      marginRight: 5,
      transition: 'color 0.2s',
      color: (props: StyleProps) => standardColor(props),
    },
    arrowIcon: {
      color: (props: StyleProps) => standardColor(props),
      marginLeft: 5,
      transition: 'color 0.2s',
      fontSize: '12px',
      marginRight: 8,
    },
    clearButton: {
      display: 'flex',
      alignItems: 'center',
      cursor: (props: StyleProps) => (props.isOpen ? 'pointer' : 'inherit'),
      borderLeft: (props: StyleProps) => (props.isOpen ? '1px solid rgba(0,0,0,0.12)' : 'none'),
      padding: '6px 12px 6px 12px',
      backgroundColor: theme.palette.grey[200],
    },
    clearIcon: {
      color: colors.black54,
      cursor: 'pointer',
      backgroundColor: theme.palette.grey[200],
    },
    input: {
      marginLeft: theme.spacing(1),
      flex: 1,
      fontSize: '14px',
    },
    searchButton: {
      borderRadius: '5px',
      textTransform: 'none',
      width: '74px',
      height: '24px',
      fontWeight: 600,
      boxShadow: 'none',
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(1),
      backgroundColor: (props: StyleProps) => (props.loading ? theme.palette.primary.main : theme.palette.grey[200]),
      '&.MuiButton-root': {
        color: 'white',
      },
    },
    stopButton: {
      borderRadius: '5px',
      textTransform: 'none',
      width: '60px',
      height: '24px',
      fontWeight: 600,
      boxShadow: 'none',
      backgroundColor: alpha(theme.palette.primary.main, 0.15),
      color: theme.palette.primary.main,
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(1),
      border: '1px solid rgba(0,0,0,0.12)',
    },
    label: {
      fontWeight: 400,
      fontSize: '14px',
      transition: 'color 0.2s',
      color: (props: StyleProps) => standardColor(props),
      userSelect: 'none',
      whiteSpace: 'nowrap',
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      cursor: (props: StyleProps) => (props.isOpen ? 'pointer' : 'inherit'),
      borderRight: (props: StyleProps) => (props.isOpen ? '1px solid rgba(0,0,0,0.12)' : 'none'),
      padding: '6px 2px 6px 12px',
    },
    betaTag: {
      marginLeft: 6,
      marginRight: 3,
      padding: '1px 6px',
      color: theme.palette.primary.main,
      border: `0.1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
      backgroundColor: '#F7F7F7',
      borderRadius: 10,
      fontSize: 12,
      fontWeight: 500,
    },
  });
});

const AskAI: React.FC<AskAIProps> = ({
  onSearch,
  onStop,
  open,
  setOpen,
  valid,
  loading,
  currentValue,
  isMobileView,
  tableWidth,
}) => {
  const [inputValue, setInputValue] = useState<string>(currentValue ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const { translate } = useContext(LanguageContext);
  const iconOnly = (tableWidth && tableWidth < 1335) || isMobileView || false;
  const classes = useStyles({ isOpen: open, valid, loading, isMobileView, currentValue, iconOnly });
  const theme = useTheme();
  const { hasAiAnalysisAccess } = useUserInfo();

  // Auto-focus logic
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (valid !== false) {
      setInputValue(currentValue ?? '');
    }
  }, [currentValue, valid]);

  const handleSearchTrigger = () => {
    if (onSearch) {
      onSearch(inputValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    setInputValue(currentValue ?? '');
  };

  const handleOpen = () => {
    if (!open) setOpen(true);
  };

  if (!hasAiAnalysisAccess) {
    return <></>;
  }

  return (
    <Paper elevation={0} className={classes.container} onClick={handleOpen}>
      <div className={classes.button} onClick={open ? handleClose : undefined}>
        {loading ? (
          <CircularProgress size={14} style={{ marginRight: 5 }} />
        ) : (
          <FontAwesomeIcon icon={faSparkles} className={classes.icon} />
        )}
        {!isMobileView && !iconOnly && (
          <Typography variant="body1" className={classes.label}>
            {translate('AI Search')}
          </Typography>
        )}
        {open && <Typography className={classes.betaTag}>Beta</Typography>}
        <FontAwesomeIcon icon={open ? faAngleLeft : faAngleRight} className={classes.arrowIcon} />
      </div>
      {open && (
        <>
          <InputBase
            inputRef={inputRef}
            placeholder={translate('Describe how to filter the table to show relevant records')}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={classes.input}
            disabled={loading}
          />
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {!loading ? (
              <Button
                color="secondary"
                size="small"
                disabled={loading || inputValue.trim() === ''}
                className={classes.searchButton}
                style={{
                  backgroundColor: inputValue.trim() !== '' ? theme.palette.primary.main : theme.palette.grey[200],
                }}
                onClick={e => {
                  e.stopPropagation();
                  handleSearchTrigger();
                }}>
                {translate('Generate')}
              </Button>
            ) : (
              <Button
                color="secondary"
                size="small"
                className={classes.stopButton}
                onClick={e => {
                  e.stopPropagation();
                  onStop?.();
                }}>
                {translate('Stop')}
              </Button>
            )}
          </div>
          <div
            className={classes.clearButton}
            onClick={() => {
              setInputValue('');
              onSearch?.('');
            }}>
            <FontAwesomeIcon icon="x" className={classes.clearIcon} />
          </div>
        </>
      )}
    </Paper>
  );
};

export default AskAI;
